import { useEffect, useMemo, useState } from 'react';
import { CenterScreenModal, Button } from '../atoms';
import { NormalFormTextField, NormalFormDateField } from '../molecules';
import { InventoryItemsTable } from './InventoryItemsTable';
import { useErrorHandlerContext, useInventoryFormModalContext, usePropertyDetailContext, useUserLanguage } from '@/customHooks';
import {
  FileOriginOption,
  FileTypeOption,
  InventoryInspectionItemStatusOption,
  InventoryInspectionTypeOption,
  useCreateInventoryInspectionMutation,
  useGetFileUploadUrlsMutation,
  useGenerateInventoryInspectionPdfMutation,
  useUpdateInventoryInspectionMutation,
  useUpsertInventoryInspectionSectionMutation,
  useUpsertInventoryInspectionItemMutation,
  type UpsertInventoryInspectionItemInput,
} from '@/graphql/generated';
import type {
  InventoryFormModalProps,
  InventoryFormModalTexts,
  InventoryItemRow,
  InventorySectionConfig,
  InventoryInspectionItemStatus,
  SectionedItemsState,
  InventoryFormState,
  InventoryInspectionItem,
  InventoryInspectionSection,
  InventoryInspectionLike,
  UpsertSectionResponse,
} from '@/custom-types';
import textsData from '@/textData/InventoryFormModalTexts.json';
import { downloadFileFromUrl } from '@/helpers';

export const InventoryFormModal = ({ sectionsConfig }: InventoryFormModalProps) => {
  const userLanguage = useUserLanguage();
  const texts: InventoryFormModalTexts = userLanguage === 'es' ? textsData.es : textsData.en;

  const { open: errorHandle } = useErrorHandlerContext();
  const { property, setProperty } = usePropertyDetailContext();

  const { isOpen, closeModal, mode, propertyId, inventory, isReadOnly } = useInventoryFormModalContext();

  const [formState, setFormState] = useState<InventoryFormState>({
    description: '',
    inspectionType: InventoryInspectionTypeOption.Initial,
    inspectionDate: '',
    inspectorName: '',
    receiverName: '',
    generalNotes: '',
  });
  const [sections, setSections] = useState<SectionedItemsState>({});
  const [unassignedItems, setUnassignedItems] = useState<InventoryItemRow[]>([]);
  const [activeTab, setActiveTab] = useState<string>('unassigned');

  const [createInspection, { loading: createInspectionLoading }] = useCreateInventoryInspectionMutation();

  const [updateInspection, { loading: updateInspectionLoading }] = useUpdateInventoryInspectionMutation();

  const [upsertSection] = useUpsertInventoryInspectionSectionMutation();

  const [upsertItem] = useUpsertInventoryInspectionItemMutation();
  const [getUploadUrls] = useGetFileUploadUrlsMutation();

  const [generatePdf, { loading: generatePdfLoading }] = useGenerateInventoryInspectionPdfMutation();

  const isLoading = createInspectionLoading || updateInspectionLoading;

  const allSections = useMemo(
    () => [...sectionsConfig],
    [sectionsConfig],
  );

  const handleFormFieldChange = <K extends keyof InventoryFormState>(field: K, value: InventoryFormState[K]) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const buildInitialFormState = (today: string): InventoryFormState => ({
    description: '',
    inspectionType: InventoryInspectionTypeOption.Initial,
    inspectionDate: today,
    inspectorName: '',
    receiverName: '',
    generalNotes: '',
  });

  const buildEmptySections = (list: InventorySectionConfig[]): SectionedItemsState => {
    const base: SectionedItemsState = {};
    list.forEach((s) => {
      base[s.id] = [];
    });
    return base;
  };

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    if (!isOpen) {
      setFormState(buildInitialFormState(today));
      setSections(buildEmptySections(allSections));
      setUnassignedItems([]);
      setActiveTab(allSections[0]?.id ?? 'unassigned');
      return;
    }

    if (mode === 'edit' && inventory) {
      const editableInventory = inventory as InventoryInspectionLike;
      setFormState({
        description: '',
        inspectionType:
          editableInventory.type === InventoryInspectionTypeOption.Final
            ? InventoryInspectionTypeOption.Final
            : InventoryInspectionTypeOption.Initial,
        inspectionDate: editableInventory.inspectionDate.slice(0, 10),
        inspectorName: editableInventory.inspectorName ?? '',
        receiverName: editableInventory.receiverName ?? '',
        generalNotes: editableInventory.generalNotes ?? '',
      });

      const newSections = buildEmptySections(allSections);
      const newUnassigned: InventoryItemRow[] = [];

      editableInventory.sections.forEach((section: InventoryInspectionSection) => {
        const config = allSections.find((s) => s.id === section.key);
        if (!config) {
          section.items.forEach((item: InventoryInspectionItem) => {
            newUnassigned.push({
              id: item.id,
              description: item.title,
              quantity: item.quantity ? Number(item.quantity) : '',
              status: item.status as InventoryInspectionItemStatus,
              notes: item.notes ?? '',
              existingFiles: item.files ?? [],
              newFiles: [],
              shouldReplaceFiles: false,
            });
          });
          return;
        }

        section.items.forEach((item: InventoryInspectionItem) => {
          newSections[config.id].push({
            id: item.id,
            description: item.title,
            quantity: item.quantity ? Number(item.quantity) : '',
            status: item.status as InventoryInspectionItemStatus,
            notes: item.notes ?? '',
            existingFiles: item.files ?? [],
            newFiles: [],
            shouldReplaceFiles: false,
          });
        });
      });

      setSections(newSections);
      setUnassignedItems(newUnassigned);
      setActiveTab(allSections[0]?.id ?? 'unassigned');
    } else {
      setFormState(buildInitialFormState(today));
      setSections(buildEmptySections(allSections));
      setUnassignedItems([]);
      setActiveTab(allSections[0]?.id ?? 'unassigned');
    }
  }, [isOpen, mode, inventory, sectionsConfig, allSections]);

  const handleSubmit = async () => {
    try {
      if (!propertyId) return;

      let inspectionId = inventory?.id ?? '';

      if (mode === 'create') {
        const created = await createInspection({
          variables: {
            propertyId,
            input: {
              type: formState.inspectionType,
              inspectionDate: formState.inspectionDate,
              inspectorName: formState.inspectorName,
              receiverName: formState.receiverName,
              generalNotes: formState.generalNotes,
            },
          },
        });
        const createdInspection = created.data?.createInventoryInspection;
        if (!createdInspection) return;
        inspectionId = createdInspection.id;
      } else if (mode === 'edit' && inventory) {
        await updateInspection({
          variables: {
            id: inventory.id,
            propertyId,
            input: {
              type: formState.inspectionType,
              inspectionDate: formState.inspectionDate || undefined,
              inspectorName: formState.inspectorName || undefined,
              receiverName: formState.receiverName || undefined,
              generalNotes: formState.generalNotes || undefined,
            },
          },
        });
        inspectionId = inventory.id;
      }

      if (!inspectionId) return;

      // === Fase 3: upsert de todas las secciones en paralelo ===
      const existingSectionsByKey: Record<string, string> =
        mode === 'edit' && inventory
          ? (inventory as InventoryInspectionLike).sections.reduce<Record<string, string>>((acc: Record<string, string>, s: InventoryInspectionSection) => {
              acc[s.key] = s.id;
              return acc;
            }, {})
          : {};

      const allSectionDefs = [
        ...allSections.map((sc, idx) => ({
          key: sc.id,
          id: existingSectionsByKey[sc.id],
          title: sc.title,
          order: idx,
          isSystem: sectionsConfig.some((s) => s.id === sc.id),
        })),
        {
          key: 'unassigned',
          id: undefined as string | undefined,
          title: 'Sin describir',
          order: allSections.length,
          isSystem: false,
        },
      ];

      const sectionResults = await Promise.all(
        allSectionDefs.map((input) =>
          upsertSection({ variables: { inspectionId, propertyId, input } }),
        ),
      );

      const sectionKeyToId: Record<string, string> = {};
      sectionResults.forEach((res, i) => {
        const def = allSectionDefs[i];
        const sectionsFromServer = (res.data?.upsertInventoryInspectionSection.sections ?? []) as UpsertSectionResponse[];
        const serverSection =
          sectionsFromServer.find((s: UpsertSectionResponse) => s.key === def.key) ??
          sectionsFromServer.find((s: UpsertSectionResponse) => s.id === def.id) ??
          sectionsFromServer.find((s: UpsertSectionResponse) => s.title === def.title);
        if (serverSection) {
          sectionKeyToId[def.key] = serverSection.id;
        }
      });

      const uploadItemFiles = async (files: File[]): Promise<string[]> => {
        if (files.length === 0) return [];

        const getUploadUrlsResponse = await getUploadUrls({
          variables: {
            inputs: files.map((file) => ({
              contentType: file.type || 'image/jpeg',
              fileExtension: (file.name.split('.').pop() ?? 'jpg').toLowerCase(),
              fileTypeOption: FileTypeOption.Picture,
              fileOriginOption: FileOriginOption.Property,
              fileSize: file.size.toString(),
            })),
          },
        });

        const uploadDataList = getUploadUrlsResponse.data?.getUploadUrls;
        if (!uploadDataList || uploadDataList.length !== files.length) {
          throw new Error('No se pudieron obtener las URLs para subir las imágenes del inventario.');
        }

        const uploadPromises = uploadDataList.map((uploadData, index) => {
          if (!uploadData.uploadUrl) {
            throw new Error('URL de subida no disponible');
          }
          return fetch(uploadData.uploadUrl, {
            method: 'PUT',
            body: files[index],
            headers: { 'Content-Type': files[index].type || 'image/jpeg' },
          });
        });

        await Promise.all(uploadPromises);
        return uploadDataList.map((uploadData) => uploadData.id);
      };

      const buildItemInput = async (
        row: InventoryItemRow,
        order: number,
      ): Promise<UpsertInventoryInspectionItemInput | null> => {
        if (!row.description || !row.quantity || row.quantity <= 0) return null;
        const statusMap: Record<InventoryInspectionItemStatus, InventoryInspectionItemStatusOption> = {
          EXCELLENT: InventoryInspectionItemStatusOption.Excellent,
          GOOD: InventoryInspectionItemStatusOption.Good,
          REGULAR: InventoryInspectionItemStatusOption.Regular,
          BAD: InventoryInspectionItemStatusOption.Bad,
          MISSING: InventoryInspectionItemStatusOption.Missing,
          NOT_EVALUATED: InventoryInspectionItemStatusOption.NotEvaluated,
        };
        const baseInput: UpsertInventoryInspectionItemInput = {
          id: row.id,
          title: row.description,
          quantity: String(row.quantity),
          status: statusMap[row.status ?? 'NOT_EVALUATED'],
          order,
          notes: row.notes?.trim() ? row.notes.trim() : undefined,
        };

        if (!row.shouldReplaceFiles) return baseInput;

        const existingFileIds = (row.existingFiles ?? []).map((file) => file.id);
        const uploadedNewFileIds = await uploadItemFiles(row.newFiles ?? []);

        return {
          ...baseInput,
          uploadedFilesIds: [...existingFileIds, ...uploadedNewFileIds],
        };
      };

      const itemPromises: Promise<unknown>[] = [];

      allSections.forEach((sc) => {
        const sectionId = sectionKeyToId[sc.id];
        if (!sectionId) return;
        (sections[sc.id] ?? []).forEach((row, idx) => {
          itemPromises.push(
            (async () => {
              const input = await buildItemInput(row, idx);
              if (!input) return;
              return upsertItem({ variables: { sectionId, propertyId, input } });
            })(),
          );
        });
      });

      const unassignedSectionId = sectionKeyToId['unassigned'];
      if (unassignedSectionId) {
        unassignedItems.forEach((row, idx) => {
          itemPromises.push(
            (async () => {
              const input = await buildItemInput(row, idx);
              if (!input) return;
              return upsertItem({ variables: { sectionId: unassignedSectionId, propertyId, input } });
            })(),
          );
        });
      }

      await Promise.all(itemPromises);

      if (property && propertyId === property.id) {
        const currentProperty = { ...property };
        setProperty(undefined);
        queueMicrotask(() => setProperty(currentProperty));
      }

      closeModal();
    } catch (error) {
      errorHandle(error);
    }
  };

  const title = mode === 'create' ? texts.titleNew : texts.titleEdit;

  const canDownloadPdf = Boolean(propertyId && inventory?.id);

  const handleDownloadPdf = async () => {
    try {
      if (!propertyId || !inventory?.id) return;
      if (generatePdfLoading) return;

      const { data } = await generatePdf({
        variables: { propertyId, inspectionId: inventory.id },
      });

      const payload = data?.generateInventoryInspectionPdf;
      if (!payload?.url) return;

      await downloadFileFromUrl(payload.url, payload.fileName ?? 'inspection.pdf');
    } catch (e) {
      errorHandle(e);
    }
  };

  return (
    <CenterScreenModal isOpen={isOpen} onClose={closeModal}>
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold">{title}</h2>
        </div>

        {/* Header de inspección */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1.3fr)_minmax(0,0.7fr)_minmax(0,1.2fr)]">
            <div>
              <p className="mb-2 font-semibold">Tipo de inspección</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  secondary={formState.inspectionType !== InventoryInspectionTypeOption.Initial}
                  onClick={() =>
                    !isReadOnly &&
                    handleFormFieldChange('inspectionType', InventoryInspectionTypeOption.Initial)
                  }
                >
                  Entrega inicial
                </Button>
                <Button
                  secondary={formState.inspectionType !== InventoryInspectionTypeOption.Final}
                  onClick={() =>
                    !isReadOnly &&
                    handleFormFieldChange('inspectionType', InventoryInspectionTypeOption.Final)
                  }
                >
                  Retorno / Final
                </Button>
              </div>
            </div>
            <div>
              <NormalFormDateField
                label="Fecha"
                name="inspectionDate"
                onChange={(value) => handleFormFieldChange('inspectionDate', value ?? '')}
                required
                value={formState.inspectionDate}
                readOnly={isReadOnly}
              />
            </div>
            <div>
              <NormalFormTextField
                label="Dirección"
                name="inspectionAddress"
                value={
                  property
                    ? `${property.address.street ?? ''} ${property.address.buildingNumber ?? ''} ${
                        property.address.city?.name ?? ''
                      }`
                    : ''
                }
                readOnly
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <NormalFormTextField
                label="Inspector / Entrega"
                name="inspectorName"
                onChange={(value) => handleFormFieldChange('inspectorName', value ?? '')}
                value={formState.inspectorName}
                readOnly={isReadOnly}
              />
            </div>
            <div>
              <NormalFormTextField
                label="Receptor / Recibe"
                name="receiverName"
                onChange={(value) => handleFormFieldChange('receiverName', value ?? '')}
                value={formState.receiverName}
                readOnly={isReadOnly}
              />
            </div>
          </div>
        </div>

        {/* Descripción general del inventario */}
        <div>
          <NormalFormTextField
            label={texts.labels.description}
            name="inventoryDescription"
            onChange={(value) => handleFormFieldChange('description', value ?? '')}
            required
            value={formState.description}
            readOnly={isReadOnly}
          />
        </div>

        {/* Tabs de secciones */}
        <div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {allSections.map((section) => (
              <Button
                key={section.id}
                secondary={activeTab !== section.id}
                onClick={() => setActiveTab(section.id)}
              >
                {section.title}
              </Button>
            ))}
            <Button
              secondary={activeTab !== 'unassigned'}
              onClick={() => setActiveTab('unassigned')}
            >
              Sección sin describir
            </Button>
          </div>
        </div>

        {/* Lista de items por sección */}
        <div className="space-y-4">
          <p className="font-semibold">{texts.labels.items}</p>
          {allSections.map((section) =>
            activeTab === section.id ? (
              <InventoryItemsTable
                key={section.id}
                items={sections[section.id] ?? []}
                onChange={(updated) => setSections((prev) => ({ ...prev, [section.id]: updated }))}
                texts={texts}
                isReadOnly={isReadOnly}
              />
            ) : null,
          )}
          {activeTab === 'unassigned' && (
            <InventoryItemsTable
              items={unassignedItems}
              onChange={setUnassignedItems}
              texts={texts}
              isReadOnly={isReadOnly}
            />
          )}
        </div>

        {/* Notas generales del informe */}
        <div>
          <NormalFormTextField
            label="Notas generales del informe"
            name="inventoryGeneralNotes"
            onChange={(value) => handleFormFieldChange('generalNotes', value ?? '')}
            value={formState.generalNotes}
            readOnly={isReadOnly}
          />
        </div>

        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button secondary onClick={closeModal}>
              {texts.buttons.back}
            </Button>
            {isReadOnly && canDownloadPdf && (
              <Button secondary onClick={handleDownloadPdf} isLoading={generatePdfLoading}>
                {texts.buttons.downloadPdf}
              </Button>
            )}
          </div>
          {!isReadOnly && (
            <Button onClick={handleSubmit} isLoading={isLoading}>
              {texts.buttons.accept}
            </Button>
          )}
        </div>
      </div>
    </CenterScreenModal>
  );
};

